// Helpery dat dla SearchForm.
//
// Cel: nigdy nie powodować hydration mismatch ani "skoku o jeden dzień" tuż po
// północy. Zasady:
//   1. Wszystkie obliczenia robimy w UTC (toISOString) — ta sama wartość
//      niezależnie od strefy klienta i serwera.
//   2. SSR NIE renderuje konkretnej daty (komponent renderuje placeholder
//      "Wybierz datę" do hydratacji), więc nawet jeśli klient i serwer są
//      w różnych dobach UTC, nic się nie rozjedzie.
//   3. Funkcje są pure (przyjmują "now"), więc da się je przetestować
//      deterministycznie w testach jednostkowych poniżej.

export function isoDateUtc(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function startOfUtcDay(now: Date = new Date()): Date {
  const d = new Date(now);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

export function addDaysIso(now: Date, days: number): string {
  return isoDateUtc(new Date(startOfUtcDay(now).getTime() + days * 86400_000));
}

export function defaultDepartIso(now: Date = new Date()): string {
  return addDaysIso(now, 7);
}

export function defaultReturnIso(now: Date = new Date()): string {
  return addDaysIso(now, 14);
}

// Walidacja po hydratacji: jeżeli z jakiegoś powodu trafiła do nas data
// w przeszłości (np. z URL-a, ze starego cache), podmień ją na bezpieczną
// wartość domyślną. Chroni przed wysłaniem zapytania z dd<today.
export function ensureFutureDate(iso: string, now: Date = new Date()): string {
  if (!iso) return defaultDepartIso(now);
  const todayIso = isoDateUtc(startOfUtcDay(now));
  return iso < todayIso ? defaultDepartIso(now) : iso;
}

// ---------------------------------------------------------------------------
// Smoke tests — uruchamiane jako `bun src/lib/search-dates.ts`.
// Trzymamy je w tym samym pliku, żeby nie wymagać setupu vitest/jest dla
// projektu, który nie ma jeszcze runnera testów.
// ---------------------------------------------------------------------------

function assertEq(actual: unknown, expected: unknown, msg: string): void {
  if (actual !== expected) {
    console.error(`❌ ${msg}\n   expected: ${expected}\n   actual:   ${actual}`);
    process.exitCode = 1;
  } else {
    console.log(`✅ ${msg}`);
  }
}

if (typeof process !== "undefined" && process.argv?.[1]?.endsWith("search-dates.ts")) {
  // Determinizm: zamrażamy "now".
  const fixed = new Date("2026-05-14T23:59:59.000Z");
  const justAfterMidnight = new Date("2026-05-15T00:00:01.000Z");

  // 1. UTC determinism — niezależnie od TZ runnera, wynik jest stały.
  assertEq(defaultDepartIso(fixed), "2026-05-21", "depart = +7 dni od UTC dnia");
  assertEq(defaultReturnIso(fixed), "2026-05-28", "return = +14 dni od UTC dnia");

  // 2. Granica północy — wartość zmienia się dokładnie po przekroczeniu doby UTC.
  assertEq(defaultDepartIso(justAfterMidnight), "2026-05-22", "po północy UTC depart przesuwa się o 1 dzień");

  // 3. ensureFutureDate broni przed datami w przeszłości.
  assertEq(ensureFutureDate("2020-01-01", fixed), "2026-05-21", "data w przeszłości → fallback do +7");
  assertEq(ensureFutureDate("2026-12-25", fixed), "2026-12-25", "data w przyszłości pozostaje");
  assertEq(ensureFutureDate("", fixed), "2026-05-21", "pusty input → fallback do +7");

  // 4. addDaysIso jest spójne między dwoma wywołaniami w tej samej "now".
  assertEq(addDaysIso(fixed, 30), "2026-06-13", "+30 dni stabilnie");
  assertEq(addDaysIso(fixed, 30), addDaysIso(fixed, 30), "idempotencja");

  console.log("\nGotowe.");
}
